/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { buttonStyle } from 'components/buttons/buttonStyle';
import { DatePickerField } from 'components/forms/inputs/common/DatePickerField';
import { Button } from 'components/ui/button';
import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    startOfYesterday,
    subMonths,
    subWeeks,
} from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import React, { useEffect } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import { getSelectorKey } from '../../getSelectorKey.ts';
import { DateRangeFilterForm, MainFilterForm, QuickFilterForm } from '../../ListTableTypes.tsx';
import { FilterLabel } from './FilterLabel';

const quickPicks: Record<string, string> = {
    today: 'filter-sidebar.today',
    yesterday: 'filter-sidebar.yesterday',
    'current-week': 'filter-sidebar.current-week',
    'last-week': 'filter-sidebar.last-week',
    'current-month': 'filter-sidebar.current-month',
    'last-month': 'filter-sidebar.last-month',
    'current-year': 'filter-sidebar.current-year',
};

const getDateRange = (key: string) => {
    const today = new Date();
    switch (key) {
        case 'current-year':
            return { from: startOfYear(today), to: endOfDay(today) };
        case 'current-month':
            return { from: startOfMonth(today), to: endOfDay(today) };
        case 'last-month':
            return { from: startOfMonth(subMonths(today, 1)), to: endOfDay(endOfMonth(subMonths(today, 1))) };
        case 'last-week':
            return {
                from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
                to: endOfDay(endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 })),
            };
        case 'current-week':
            return { from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfDay(today) };
        case 'yesterday':
            return { from: startOfYesterday(), to: endOfDay(startOfYesterday()) };
        default:
            return { from: startOfDay(today), to: endOfDay(today) };
    }
};

/**
 * The shared time-period shortcuts under a range filter. Both range inputs render this one so a
 * date range looks the same wherever it appears, whatever the underlying column type is.
 */
const QuickPicks: React.FC<{
    filter: DateRangeFilterForm;
    onChange: (value: number | string | null, groupSelector: string, setMin: boolean) => void;
}> = ({ filter, onChange }) => {
    if (!filter.shouldDisplayQuickPick) {
        return null;
    }
    return (
        <div className="flex flex-wrap gap-3">
            {Object.keys(quickPicks).map((qp) => (
                <Button
                    key={`qp-${getSelectorKey(filter)}-${qp}`}
                    onClick={() => {
                        const range = getDateRange(qp);
                        onChange(String(range.from), getSelectorKey(filter), true);
                        onChange(String(range.to), getSelectorKey(filter), false);
                    }}
                    // Bare link-coloured words on the control height: no border to widen the row, no underline.
                    {...buttonStyle({ variant: 'text' }, 'border-0 px-0 font-normal hover:no-underline')}
                >
                    {t(quickPicks[qp])}
                </Button>
            ))}
        </div>
    );
};

/** The captions over a range's two fields, each over its own half of the row. */
const RangeLabels: React.FC<{ filter: DateRangeFilterForm }> = ({ filter }) => (
    <div className="flex w-full flex-row gap-4">
        {filter.label && (
            <div className="w-full grow">
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues} />
            </div>
        )}
        {filter.labelTo && (
            <div className="w-full grow">
                <FilterLabel label={filter.labelTo} tooltip={filter.tooltipTo} tooltipValues={filter.tooltipValues} />
            </div>
        )}
    </div>
);

export const DateRange: React.FC<{
    filter: DateRangeFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: number | string | null, groupSelector: string, setMin: boolean) => void;
    /** The two dates one above the other, for a narrow pane. */
    stacked?: boolean;
}> = React.memo(({ filter, onChange, quickFilters, mainFilters, stacked }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && (filter.from != null || filter.to != null)) {
            onChange(null, getSelectorKey(filter), true);
            onChange(null, getSelectorKey(filter), false);
        }
    }, [filter, isHidden, onChange]);

    if (isHidden) {
        return null;
    }

    return (
        <div className="flex w-full grow flex-col gap-2" key={`filter-${getSelectorKey(filter)}`}>
            <RangeLabels filter={filter} />
            <div className="flex flex-col gap-1">
                <div className={cn('flex w-full gap-4', stacked && 'flex-col gap-2')}>
                    <div className="flex-1">
                        <DatePickerField
                            format={effectiveFormat}
                            value={filter.from ? new Date(filter.from) : null}
                            onChange={(val: Date | null) => {
                                onChange(val !== null ? String(val) : null, getSelectorKey(filter), true);
                            }}
                            placeholder={t('forms.select-date-start')}
                        />
                    </div>
                    <div className="flex-1">
                        <DatePickerField
                            format={effectiveFormat}
                            value={filter.to ? new Date(filter.to) : null}
                            onChange={(val: Date | null) => {
                                onChange(val !== null ? String(val) : null, getSelectorKey(filter), false);
                            }}
                            placeholder={t('forms.select-date-end')}
                        />
                    </div>
                </div>
                <QuickPicks filter={filter} onChange={onChange} />
            </div>
        </div>
    );
});

export const OffsetDateTimeRange: React.FC<{
    filter: DateRangeFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: number | string | null, groupSelector: string, setMin: boolean) => void;
}> = React.memo(({ filter, onChange, quickFilters, mainFilters }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && (filter.from != null || filter.to != null)) {
            onChange(null, getSelectorKey(filter), true);
            onChange(null, getSelectorKey(filter), false);
        }
    }, [filter, isHidden, onChange]);

    if (isHidden) {
        return null;
    }
    return (
        <div className="flex w-full grow flex-col gap-2" key={`filter-${getSelectorKey(filter)}`}>
            <RangeLabels filter={filter} />
            <div className="flex flex-col gap-1">
                <div className="flex w-full gap-4">
                    <div className="flex-1">
                        <DatePickerField
                            format={effectiveFormat}
                            value={filter.from ? new Date(filter.from) : null}
                            onChange={(val: Date | null) => {
                                val?.setHours(0, 0, 0, 0);
                                onChange(val !== null ? String(val) : null, getSelectorKey(filter), true);
                            }}
                            placeholder={t('forms.select-date-start')}
                        />
                    </div>
                    <div className="flex-1">
                        <DatePickerField
                            format={effectiveFormat}
                            value={filter.to ? new Date(filter.to) : null}
                            onChange={(val: Date | null) => {
                                val?.setHours(23, 59, 59, 999);
                                onChange(val !== null ? String(val) : null, getSelectorKey(filter), false);
                            }}
                            placeholder={t('forms.select-date-end')}
                        />
                    </div>
                </div>
                <QuickPicks filter={filter} onChange={onChange} />
            </div>
        </div>
    );
});
